import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Lock, ChevronRight, CheckCircle2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);

  useEffect(() => {
    api.get(`/courses/${id}`).then(({ data }) => setCourse(data)).catch(() => {});
    if (user) api.post("/track", { event: "course_opened", source: "course", meta: { id } }).catch(() => {});
  }, [id, user]);

  if (!course) return <div className="p-8"><div className="h-8 w-56 bg-chessup-card rounded animate-pulse" /></div>;

  const isPremium = user?.is_premium;

  return (
    <div className="max-w-5xl mx-auto p-5 sm:p-8">
      <Link to="/cursos" className="inline-flex items-center gap-1 text-sm text-chessup-subtext hover:text-chessup-turq mb-6"><ArrowLeft className="h-4 w-4" /> Voltar aos cursos</Link>

      <div className="rounded-2xl border border-chessup-border overflow-hidden mb-8">
        <div className="relative aspect-[21/9]">
          <img src={course.cover} alt={course.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-chessup-deep via-chessup-deep/40 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <span className="text-xs text-chessup-turq font-mono uppercase">{course.difficulty}</span>
            <h1 className="font-heading text-3xl text-chessup-text mt-1">{course.title}</h1>
            <p className="text-sm text-chessup-subtext mt-2 max-w-2xl">{course.description}</p>
          </div>
        </div>
      </div>

      <h2 className="font-heading text-xl text-chessup-text mb-4">Conteúdo do curso</h2>
      <div className="space-y-5">
        {course.modules.map((m) => (
          <div key={m.id} className="rounded-xl border border-chessup-border bg-chessup-card overflow-hidden" data-testid={`module-${m.id}`}>
            <div className="px-5 py-3 border-b border-chessup-border bg-chessup-deep/40"><h3 className="font-medium text-chessup-text">{m.title}</h3></div>
            <div className="divide-y divide-chessup-border/40">
              {m.lessons.length === 0 && <div className="px-5 py-6 text-sm text-chessup-subtext text-center">Novas aulas estão chegando.</div>}
              {m.lessons.map((l) => {
                const locked = l.access !== "free" && !isPremium;
                return (
                  <Link key={l.id} to={`/aula/${l.id}`} data-testid={`lesson-row-${l.id}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-chessup-deep/30 transition-colors">
                    <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${locked ? "bg-chessup-deep text-chessup-subtext" : "bg-chessup-turq/12 text-chessup-turq"}`}>
                      {locked ? <Lock className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-chessup-text truncate">{l.lesson_number}. {l.title}</div>
                      <div className="text-xs text-chessup-subtext">{l.duration} · {l.access === "free" ? "Grátis" : "Premium"}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-chessup-subtext" />
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!isPremium && (
        <div className="mt-8 rounded-xl border border-chessup-gold/40 bg-chessup-gold/8 p-6 text-center">
          <p className="text-chessup-text">🔒 Conteúdo exclusivo para membros ChessUp Academy</p>
          <Link to="/assinatura" className="mt-4 inline-block rounded-full bg-chessup-gold px-6 py-3 font-semibold text-chessup-deep hover:bg-chessup-goldlight">ASSINAR PARA ACESSAR</Link>
        </div>
      )}
    </div>
  );
}
